import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, CalendarClock, Award, CheckCircle2 } from "lucide-react";

function todayKey() { return new Date().toISOString().slice(0, 10); }

// Encouraging copy that adapts to where the team member is in their journey.
function buildEncouragement(p) {
  const a = p.appreciations_received || 0;
  const need = p.appreciations_needed || 0;
  const daysLeft = p.days_left || 0;
  const daysIn = p.days_in || 0;

  if (need === 0 && !p.time_requirement_met) {
    return {
      title: "Almost there.",
      body: `You've earned all 3 appreciations from management — incredible. ${daysLeft} day${daysLeft === 1 ? "" : "s"} until your probation period closes and you're officially confirmed. Keep doing what you're doing.`,
    };
  }
  if (a === 0) {
    return {
      title: "You've just begun.",
      body: `You're ${daysIn} day${daysIn === 1 ? "" : "s"} into your probation. Bring your best, ask for help when you need it, and ${need} appreciation${need === 1 ? "" : "s"} from management will see you confirmed.`,
    };
  }
  if (a === 1) {
    return {
      title: "One down, two to go.",
      body: `You already have 1 appreciation in your record — that's a good start. Two more and you're confirmed. Don't slow down — go gently and finish strong.`,
    };
  }
  if (a === 2) {
    return {
      title: "So close.",
      body: `2 appreciations down. Just 1 more and you're officially part of the team for the long haul. Today is a great day to do something memorable.`,
    };
  }
  return {
    title: "Keep shining.",
    body: `Management has appreciated your work ${a} time${a === 1 ? "" : "s"} already. The bar is met — now we just wait out the calendar. Stay consistent.`,
  };
}

export default function ProbationReminderModal() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user || user === false) return;
    if (user.active === false) return;
    if (user.role === "super_admin") return;
    if (!user.terms_accepted) return; // Terms first
    if (!user.profile_completed) return; // KYC before probation reminder
    const p = user.probation_status;
    if (!p || !p.on_probation) return;
    // Show ONCE per day per user
    const k = `hr_probation_${user.id}_${todayKey()}`;
    if (localStorage.getItem(k)) return;
    // Defer slightly so it appears AFTER the mood-check modal
    const t = setTimeout(() => setOpen(true), 600);
    return () => clearTimeout(t);
  }, [user]);

  if (!user || user === false || !user.probation_status?.on_probation) return null;
  const p = user.probation_status;
  const msg = buildEncouragement(p);
  const totalSteps = 3;
  const stepsDone = Math.min(totalSteps, p.appreciations_received || 0);

  const close = () => {
    localStorage.setItem(`hr_probation_${user.id}_${todayKey()}`, "1");
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent
        className="max-w-lg p-0 overflow-hidden border-0"
        data-testid="probation-modal"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <div className="bg-gradient-to-b from-amber-50 to-white p-8 space-y-5">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 text-amber-700 mb-3">
              <Award className="w-6 h-6" strokeWidth={1.5} />
            </div>
            <span className="label-overline text-amber-700">Probation reminder</span>
            <h2 className="font-display text-3xl font-black tracking-tighter mt-2" data-testid="probation-title">{msg.title}</h2>
          </div>

          <p className="text-zinc-700 text-[15px] leading-relaxed text-center" data-testid="probation-body">
            {msg.body}
          </p>

          {/* Progress: 3 appreciation pips */}
          <div className="bg-white border border-zinc-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="label-overline">Appreciations from management</span>
              <span className="text-xs font-bold font-mono">{stepsDone} / {totalSteps}</span>
            </div>
            <div className="flex gap-2" data-testid="probation-pips">
              {Array.from({ length: totalSteps }).map((_, i) => {
                const done = i < stepsDone;
                const pipKey = `pip-${i}`;
                return (
                  <div
                    key={pipKey}
                    className={`flex-1 h-3 rounded-full transition ${done ? "bg-gradient-to-r from-emerald-400 to-emerald-600" : "bg-zinc-100"}`}
                    data-testid={`probation-pip-${i}-${done ? "done" : "todo"}`}
                  />
                );
              })}
            </div>
            <div className="flex items-center justify-between mt-3 text-[11px] text-zinc-500">
              <div className="inline-flex items-center gap-1">
                <CalendarClock className="w-3.5 h-3.5" />
                <span>Day {p.days_in} of 90</span>
              </div>
              <div className="inline-flex items-center gap-1">
                {p.time_requirement_met ? (
                  <><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /><span>Calendar bar met</span></>
                ) : (
                  <span>{p.days_left} day{p.days_left === 1 ? "" : "s"} left</span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center gap-2 text-[12px] text-zinc-500">
            <Sparkles className="w-3.5 h-3.5 text-amber-500" />
            <span>This reminder shows once per day. Keep going.</span>
          </div>

          <div className="flex justify-center pt-1">
            <Button onClick={close} className="bg-zinc-950 hover:bg-zinc-800 px-8" data-testid="probation-continue">
              Got it — let&apos;s go
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
